/**
 * Entry point for the Orkexra frontend.
 * All wiring happens in ``@/app``; this file exists only to bootstrap.
 */
import React from "react";
import ReactDOM from "react-dom/client";

import { App } from "@/app/App";

import "@/index.css";

/**
 * Pre-boot Google-OAuth hash normalisation — Patch 37.4.2.
 *
 * Emergent's OAuth handshake lands the browser on
 * ``<AUTH_LANDING>#session_id=…`` (see ``GoogleButton`` in
 * ``pages/auth/AuthPage.tsx``). If we let React mount at that URL, the
 * ``AuthGuard`` on ``/home`` observes "no token" and redirects the
 * visitor to ``/login?returnTo=/home#session_id=…`` in the SAME render
 * pass that ``SessionIdInterceptor`` tries to redirect to
 * ``/auth/callback#session_id=…``. React 18 fires the deeper
 * ``AuthGuard`` effect first — its ``<Navigate>`` wins, the hash gets
 * URL-encoded into the ``returnTo`` query, ``window.location.hash``
 * loses its ``session_id``, and the callback never runs. Symptom: an
 * infinite "Signing you in…" spinner or a "Missing session_id" error.
 *
 * Fix: normalise the URL to ``/auth/callback#session_id=…`` BEFORE
 * React renders. ``history.replaceState`` is synchronous and cheap; by
 * the time the router mounts the address bar already reads
 * ``/auth/callback`` and no guard race can strip the hash. We only
 * touch the URL when a ``session_id`` fragment is actually present and
 * we're not already on the callback route — every other flow (email /
 * password, refresh of a normal page, deep link) is untouched.
 */
(function normaliseOAuthHashBeforeMount(): void {
  try {
    if (typeof window === "undefined") return;
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    if (window.location.pathname.startsWith("/auth/callback")) return;
    window.history.replaceState(null, "", `/auth/callback${hash}`);
  } catch {
    /* If history is unavailable, fall through — the SPA-side
     * interceptor + callback page will still catch the fragment. */
  }
})();

const root = document.getElementById("root");
if (!root) {
  throw new Error("Root element #root not found");
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
